<template>
  <div class="child-devices-manager">
    <div class="card card-custom">
      <div class="card-header">
        <div class="card-title">
          <h3 class="card-label">Hub Sensors</h3>
        </div>
        <div class="card-toolbar">
          <button 
            class="btn btn-primary btn-sm"
            @click="showAddDialog = true"
          >
            <i class="la la-plus"></i> Add Sensor
          </button>
        </div>
      </div>
      
      <div class="card-body">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!childDevices.length" class="text-center py-5">
          <i class="la la-inbox icon-5x text-muted mb-3"></i>
          <p class="text-muted">No sensors connected yet</p>
        </div>

        <!-- Child Devices List -->
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Sensor Name</th>
                <th>Location</th>
                <th>Status</th>
                <th>Last Reading</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="child in childDevices" :key="child.id">
                <td>
                  <div class="d-flex align-items-center">
                     <!-- Moisture icon -->
                    <i 
                      v-if="child.version === 'soilblessv1'" 
                      class="la la-tint text-primary mr-2"
                    ></i>

                    <!-- Temperature icon -->
                    <i 
                      v-else-if="child.version === 'templessv1'" 
                      class="la la-thermometer-half text-danger mr-2"
                    ></i>
                    <span class="font-weight-bold">{{ child.object }}</span>
                  </div>
                </td>
                <td>
                  <span class="label label-inline label-light-info">
                    {{ child.location || 'Unassigned' }}
                  </span>
                </td>
                <td>
                  <span 
                    class="label label-dot mr-2"
                    :class="child.online ? 'label-success' : 'label-danger'"
                  ></span>
                  {{ child.online ? 'Online' : 'Offline' }}
                </td>
                <td>{{ formatLastReading(child) }}</td>
                <td class="text-right">
                  <button
                    class="btn btn-sm btn-icon btn-light-danger"
                    @click="confirmRemove(child)"
                    :disabled="removing === child.id"
                  >
                    <i class="la la-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add Child Device Dialog -->
    <b-modal
      v-model="showAddDialog"
      title="Add Soil Moisture Sensor"
      @ok="handleAddChild"
      @hidden="resetAddForm"
      ok-title="Add Sensor"
      :ok-disabled="!selectedChildId || adding"
    >
      <div class="form-group">
        <label>Select Sensor</label>
        <select 
          v-model="selectedChildId" 
          class="form-control"
          :disabled="adding"
        >
          <option :value="null">-- Select a sensor --</option>
          <option 
            v-for="device in availableSensors" 
            :key="device.id" 
            :value="device.id"
          >
            {{ device.object }} ({{ device.location }})
          </option>
        </select>
      </div>

      <div v-if="adding" class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="sr-only">Adding...</span>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import moment from 'moment';
import axios from 'axios';
import ApiService from "@/core/services/api.service";
import {
  GET_DEVICES,
} from "@/core/services/store/devices.module";

export default {
  name: 'ChildDevicesManager',
  
  props: {
    device: null,
  },

  data() {
    return {
      childDevices: [],
      availableSensors: [],
      loading: false,
      adding: false,
      removing: null,
      showAddDialog: false,
      selectedChildId: null
    };
  },

  mounted() {
    this.loadChildDevices();
    this.loadAvailableSensors();
  },

  methods: {
    async loadChildDevices() {
        if (!this.device) return;
      this.loading = true;
      try {
        console.log('Loading child devices for', this.device);
        const response = await axios.get(`/devices/v2/${this.device.token}/children.json`);
        this.childDevices = response.data.children || [];
      } catch (error) {
        console.error('Failed to load child devices:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load sensors'
        });
      } finally {
        this.loading = false;
      }
    },

    async loadAvailableSensors() {
      try {
        await this.$store.dispatch(GET_DEVICES, {});
        const response = this.$store.state.devices;
        console.log('All devices:', response.devices);
        
        this.availableSensors = (response.devices || []).filter(
          device => (device.version === 'soilblessv1' || device.version === 'templessv1') && 
                    !this.childDevices.find(child => child.id === device.id)
        );
        console.log("availableSensors:", this.availableSensors);
      } catch (error) {
        console.error('Failed to load available sensors:', error);
      }
    },

    async handleAddChild() {
      if (!this.selectedChildId) return;

      this.adding = true;
      try {
        await ApiService.post(`/devices/v2/${this.device.token}/children.json`, {
          child_device_id: this.selectedChildId,
          child_token: this.availableSensors.find(d => d.id === this.selectedChildId).token,
          relationship_type: 'sensor'
        });

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Sensor added successfully',
          timer: 2000,
          showConfirmButton: false
        });

        this.showAddDialog = false;
        await this.loadChildDevices();
        await this.loadAvailableSensors();
      } catch (error) {
        console.error('Failed to add child device:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.error || 'Failed to add sensor'
        });
      } finally {
        this.adding = false;
      }
    },

    confirmRemove(child) {
      Swal.fire({
        title: 'Remove Sensor?',
        text: `Are you sure you want to remove "${child.object}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#f64e60'
      }).then(result => {
        if (result.isConfirmed) {
          this.handleRemoveChild(child.id);
        }
      });
    },

    async handleRemoveChild(childId) {
      this.removing = childId;
      this.child_token = this.availableSensors.find(d => d.id === childId)?.token || null;
      try {
        await ApiService.delete(`/devices/v2/${this.device.token}/children/${this.child_token}.json`);

        Swal.fire({
          icon: 'success',
          title: 'Removed',
          text: 'Sensor removed successfully',
          timer: 2000,
          showConfirmButton: false
        });

        await this.loadChildDevices();
        await this.loadAvailableSensors();
      } catch (error) {
        console.error('Failed to remove child device:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.error || 'Failed to remove sensor'
        });
      } finally {
        this.removing = null;
      }
    },

    resetAddForm() {
      this.selectedChildId = null;
    },

    formatLastReading(device) {
      if (!device.last_reading_at) return 'No data';
      return moment(device.last_reading_at).fromNow();
    }
  },
  watch: {
    device: function(oldval, newval) {
      oldval,newval;
      console.log('Device prop changed, reloading child devices');
      this.loadChildDevices();
    },
  },
};
</script>

<style scoped>
.child-devices-manager {
  margin-top: 20px;
}

.icon-5x {
  font-size: 5rem;
}
</style>